/**
 * Web Push do widget (VAPID).
 *
 * Regras que vieram da saga de push de agosto/2026 e valem aqui:
 * - `Notification.requestPermission()` **só sob gesto** do usuário; chamado no
 *   load, o browser nega e o site fica queimado até o usuário reverter na mão.
 * - no iOS, push só existe dentro do PWA instalado (funil `?install=pwa`);
 * - todo await de API do browser tem timeout — SDK/permissão travada não pode
 *   deixar a UI presa em "carregando".
 */

export interface PushSubscriptionPayload {
  endpoint: string;
  expirationTime?: number | null;
  keys: { p256dh: string; auth: string };
}

const SUBSCRIBE_TIMEOUT_MS = 10_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => { reject(new Error(`timeout: ${label}`)); }, ms)),
  ]);
}

/**
 * A chave VAPID vem em base64url; `applicationServerKey` exige bytes.
 * O `ArrayBuffer` explícito é necessário porque a tipagem do DOM não aceita
 * `Uint8Array<ArrayBufferLike>` (que poderia ser SharedArrayBuffer).
 */
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

/** Push é suportado? (Safari/iOS fora do PWA responde false.) */
export function isPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

const DEFAULT_EMBED_SW_PATH = '/troia-push-sw.js';

/**
 * Registra o service worker de push na origem do CLIENTE (fluxo embed).
 *
 * O widget embed roda no domínio do cliente, e service worker é same-origin —
 * por isso o arquivo precisa estar hospedado lá. Se não estiver (404, ou o
 * SPA devolvendo HTML no lugar do script), devolvemos `null` e o chat segue
 * normal, apenas sem notificação em background.
 *
 * A checagem por `content-type` é deliberada: SPA com fallback devolve 200 +
 * HTML para arquivo inexistente, e registrar isso quebraria com erro obscuro
 * de MIME type.
 */
export async function registerEmbedPushWorker(path?: string): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;

  const swPath = path ?? DEFAULT_EMBED_SW_PATH;
  try {
    const head = await withTimeout(fetch(swPath, { method: 'GET' }), SUBSCRIBE_TIMEOUT_MS, 'fetch sw');
    const contentType = head.headers.get('content-type') ?? '';
    if (!head.ok || !contentType.includes('javascript')) {
      console.info('[Widget] Push indisponível no embed: hospede o troia-push-sw.js na raiz do site.');
      return null;
    }

    return await withTimeout(
      navigator.serviceWorker.register(swPath),
      SUBSCRIBE_TIMEOUT_MS,
      'serviceWorker.register',
    );
  } catch (error) {
    console.warn('[Widget] Falha ao registrar o service worker de push:', error);
    return null;
  }
}

export function getPushPermission(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Pede permissão (exige gesto do usuário no call-site) e assina o push.
 * Devolve `null` quando não há suporte, o usuário recusou ou o service worker
 * não está disponível — nesses casos o chat segue funcionando normalmente.
 */
export async function subscribeToPush(
  publicKey: string,
  /** Registration a usar. No embed vem de `registerEmbedPushWorker`; na hosted, omitido (usa o SW da própria origem). */
  swRegistration?: ServiceWorkerRegistration | null,
): Promise<PushSubscriptionPayload | null> {
  if (!isPushSupported()) return null;

  try {
    const permission = await withTimeout(Notification.requestPermission(), SUBSCRIBE_TIMEOUT_MS, 'requestPermission');
    if (permission !== 'granted') return null;

    const registration = swRegistration
      ?? await withTimeout(navigator.serviceWorker.ready, SUBSCRIBE_TIMEOUT_MS, 'serviceWorker.ready');

    // Reaproveita a assinatura existente quando a chave do servidor não mudou.
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing.toJSON() as PushSubscriptionPayload;

    const subscription = await withTimeout(
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }),
      SUBSCRIBE_TIMEOUT_MS,
      'pushManager.subscribe',
    );

    return subscription.toJSON() as PushSubscriptionPayload;
  } catch (error) {
    console.warn('[Widget] Falha ao assinar push:', error);
    return null;
  }
}

/** Chave pública VAPID do canal. `null` quando o canal não permite push. */
export async function fetchPushPublicKey(apiUrl: string, channelId: string): Promise<string | null> {
  try {
    const response = await fetch(`${apiUrl}/api/widget/push-config/${channelId}`);
    if (!response.ok) return null;
    const body = (await response.json()) as { data?: { publicKey?: string } };
    return body.data?.publicKey ?? null;
  } catch {
    return null;
  }
}
